import { PERMISSIONS } from '@repo/contracts';
import { lazy } from 'react';
import type { RouteObject } from 'react-router';

import { AppShell } from '@/app/app-shell';
import { APP_ROUTES } from '@/constants/app-routes';

import { RequireAuth } from './require-auth';
import { RequirePermission } from './require-permission';

const LoginPage = lazy(() => import('@/pages/login/LoginPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/NotFoundPage'));
const RouteErrorPage = lazy(() => import('@/pages/route-error/RouteErrorPage'));
const HomePage = lazy(() => import('@/pages/home/HomePage'));
const MyAssignmentPage = lazy(() => import('@/pages/taking/MyAssignmentPage'));
const AttemptPage = lazy(() => import('@/pages/taking/AttemptPage'));
const QuestionnairesPage = lazy(() => import('@/pages/questionnaires/QuestionnairesPage'));
const QuestionnaireEditorPage = lazy(
  () => import('@/pages/questionnaires/editor/QuestionnaireEditorPage'),
);
const AssignmentsPage = lazy(() => import('@/pages/assignments/AssignmentsPage'));
const AssignmentDetailPage = lazy(() => import('@/pages/assignments/AssignmentDetailPage'));
const NewAssignmentPage = lazy(() => import('@/pages/assignments/NewAssignmentPage'));
const ReviewQueuePage = lazy(() => import('@/pages/review/ReviewQueuePage'));
const GradingPage = lazy(() => import('@/pages/review/GradingPage'));
const MyCertificatesPage = lazy(() => import('@/pages/certificates/MyCertificatesPage'));
const AllCertificatesPage = lazy(() => import('@/pages/certificates/AllCertificatesPage'));
const CertificateDetailPage = lazy(() => import('@/pages/certificates/CertificateDetailPage'));
const GroupsPage = lazy(() => import('@/pages/groups/GroupsPage'));
const GroupDetailPage = lazy(() => import('@/pages/groups/GroupDetailPage'));
const DepartmentsPage = lazy(() => import('@/pages/departments/DepartmentsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));

export const routes: RouteObject[] = [
  { path: APP_ROUTES.login, element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    element: <RequireAuth />,
    // Last resort: only reached if AppShell itself fails to render, so it
    // renders RouteErrorPage without the sidebar chrome that just broke.
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            // A pathless layout route so the boundary sits inside AppShell:
            // an error here keeps the sidebar, instead of errorElement on
            // AppShell itself replacing the whole shell.
            errorElement: <RouteErrorPage />,
            children: [
              { path: APP_ROUTES.home, element: <HomePage /> },
              // A person's own training needs a session and no permission; the service scopes it.
              { path: APP_ROUTES.myAssignment, element: <MyAssignmentPage /> },
              { path: APP_ROUTES.attempt, element: <AttemptPage /> },
              { path: APP_ROUTES.certificates, element: <MyCertificatesPage /> },
              {
                element: <RequirePermission permission={PERMISSIONS.certificateRead} />,
                children: [
                  { path: APP_ROUTES.allCertificates, element: <AllCertificatesPage /> },
                  { path: APP_ROUTES.certificate, element: <CertificateDetailPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.questionnaireRead} />,
                children: [
                  { path: APP_ROUTES.questionnaires, element: <QuestionnairesPage /> },
                  { path: APP_ROUTES.questionnaire, element: <QuestionnaireEditorPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.trainingAssignmentRead} />,
                children: [
                  { path: APP_ROUTES.assignments, element: <AssignmentsPage /> },
                  { path: APP_ROUTES.assignment, element: <AssignmentDetailPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.trainingAssignmentCreate} />,
                children: [{ path: APP_ROUTES.newAssignment, element: <NewAssignmentPage /> }],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.trainingAttemptApprove} />,
                children: [
                  { path: APP_ROUTES.review, element: <ReviewQueuePage /> },
                  { path: APP_ROUTES.reviewAttempt, element: <GradingPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.trainingGroupRead} />,
                children: [
                  { path: APP_ROUTES.groups, element: <GroupsPage /> },
                  { path: APP_ROUTES.group, element: <GroupDetailPage /> },
                  { path: APP_ROUTES.departments, element: <DepartmentsPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.trainingSettingsRead} />,
                children: [{ path: APP_ROUTES.settings, element: <SettingsPage /> }],
              },
            ],
          },
          // Last child, so it never shadows a real route above it.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
